import { Platform } from "@angular/cdk/platform";
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { NbDialogService } from "@nebular/theme";
import { AgGridAngular } from "ag-grid-angular";
import { ColDef, ColumnApi, GridApi, RowNode } from "ag-grid-community";
import { Subscription } from "rxjs";
import { PageResult } from "../../model/page-result";
import { Objects } from "../../util/objects";
import { Strings } from "../../util/Strings";
import { ColumnOptionComponent } from "../column-option/column-option.component";
import { CsvComponent } from "../csv/csv.component";
import { AG_GRID_LOCALE_KR } from "./local.kr";

@Component({
  selector: "ngx-dynamic-data-grid",
  templateUrl: "./dynamic-data-grid.component.html",
  styleUrls: ["./dynamic-data-grid.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DynamicDataGridComponent implements OnInit, OnDestroy {

  @ViewChild("grid", { static: false }) public grid: AgGridAngular;
  @ViewChild("grid", { static: false }) public gridDiv: ElementRef;

  @Input("showOption") public showOption: boolean = false; // 컨텍스트 메뉴 표시여부
  @Input("showColumnOption") public showColumnOption: boolean = true; // 컨텍스트 메뉴내 컬럼옵션 표시 유무
  @Input("showColumnFilter") public showColumnFilter: boolean = true; // 컨텍스트 메뉴내 컬럼필터  표시 유무
  @Input("showExcelExport") public showExcelExport: boolean = true; // 컨텍스트 메뉴내 CSV 내보내기 표시 유무
  @Input("showSelectAll") public showSelectAll: boolean = false; // 컨텍스트 메뉴내 전체선택 표시 유무
  @Input("showUnselectAll") public showUnselectAll: boolean = false; // 컨텍스트 메뉴내 전체선택해제 표시 유무
  @Input("showPager") public showPager: boolean = false;
  @Input("rowDoubleClickEnable") public rowDoubleClickEnable: boolean = false; // 더블클릭 이벤트 사용 여부
  @Input("rowClickEnable") public rowClickEnable: boolean = false; // 싱글클릭 이벤트 사용 여부
  @Input("headerContent") public headerContent: TemplateRef<any> = null; // 그리드 상단 컨텐츠
  @Input("footerContent") public footerContent: TemplateRef<any> = null; // 그리드 하단 컨텐츠
  @Input("rowSelection") public rowSelection = 'multiple'; // 다중선택 사용여부
  @Input("isCheckBoxChangeEvent") public isCheckBoxChangeEvent: boolean = false;
  @Input("useColumnSizeToFit") public useColumnSizeToFit: boolean = false; // 컬럼크기맞춤 기본 사용 여부
  @Input("isRowSelectable") public isRowSelectable: Function;
  @Input("rowClassRules") public rowClassRules: Function;
  @Input("parentContext") public parentContext: any; //부모의 Context 접근 (ICellRendererParams 에서 받을 context 설정: renderer 에서 부모접근용)
  @Input("overlayNoRowsTemplate") public overlayNoRowsTemplate: any; // 데이터 없는경우 표시되는 템플릿

  // AG-GRID Variable - Start

  /**
   * style.scss 선언된 스크롤바 크기 0.6125rem + 기본 여백 보정 크기
   */
   private correctionSize: number = 11;

  public defaultColDef: ColDef = {
    flex: 1,
    resizable: true,
    filter: true,
    sortable: true
  };

  /**
   * 컬럼너비 자동맞춤
   */
  public columnSizeFit: boolean = false;

  /**
   * 행을 클릭할떄 이벤트 발생여부 (true: 발생, false: 무시(default))
   * CheckBox 컬럼 사용시 해당 옵션이 true해야만 행클릭시 체크박스가 클릭되지 않음
   */
  @Input('suppressRowClickSelection') suppressRowClickSelection: boolean = true;

  /**
   * GRID 정보 업데이트 시 첫 행으로 자동 이동 (true: 발생, false: 유지)
   */
  @Input('suppressScrollOnNewData') suppressScrollOnNewData: boolean = true;

  /**
   * Cell 내 TEXT 선택여부
   */
  @Input('enableCellTextSelection') enableCellTextSelection: boolean = false;

  /**
   * 툴팁표시 딜레이 (단위: 밀리세컨드, 기본값 1초, 0이면 즉시표시)
   */
  @Input('tooltipShowDelay') tooltipShowDelay: number = 1000;

  /**
   * 컬럼별 sort기능 활성화 여부 (기본값 true)
   */
  @Input('sortable') public set sortable(value: boolean) {
    this.defaultColDef.sortable = value;
  }

  /**
   * AG GRID 국제화
   */
  public localeText = AG_GRID_LOCALE_KR;

  /**
   * 전달받은 객체에서 resultValues 및 페이징 정보를 추출하여 세팅
   */
  @Input("dataSource")
  set dataSource(dataSource: PageResult<any>) {

    if (!dataSource) return;

    if (this.gridApi)
        this.gridApi.showLoadingOverlay();

    this._dataSource = dataSource;
  }

  get dataSource(): PageResult<any> {
    return this._dataSource;
  }

  /**
   * 데이터 그리드에 표시할 컬럼 정보
   */
  @Input()
  set columns(columns) {
    this.settings.columns = columns;
    this.changedLocale();
    this.changeVisibleColumns(columns);
  }

  /**
   * CSV 내보내기 시 파일명
   */
  @Input() fileName: string = "";

  /**
   * 그리드 명 : 통합필터의 부가기능에서 표시될 그리드명
   */
  @Input() label = "";

  @Input("gridOptions") gridOptions: any;

  @Input("rowHeight") public set rowHeight(h: number) {
    this._rowHeight = h;

    if (this.gridApi) {
      this.gridApi.resetRowHeights();
      this.gridApi.onRowHeightChanged();
    }
  }

  public get rowHeight(): number {
    return this._rowHeight;
  }

  /**
   * 데이터 그리드 최초 세팅
   * actions 는 건들지 말 것.
   */
  public settings = {
    columns: []
  };

  /**
   * 페이징 기본세팅
   */
  public _dataSource: PageResult<any> = new PageResult<any>();

  public columnTypes;

  @Output() public onMultiRowSelect: EventEmitter<any> = new EventEmitter<any>(); // 다중 셀렉트 이벤트
  @Output() public rowDoubleClick: EventEmitter<any> = new EventEmitter<any>(); // 더블클릭 이벤트
  @Output() public rowClick: EventEmitter<any> = new EventEmitter<any>(); // 싱글클릭 이벤트
  @Output() public changePage: EventEmitter<any> = new EventEmitter<any>(); // 페이지 이동 이벤트
  @Output() public changePageSize: EventEmitter<any> = new EventEmitter<any>(); // 페이지 사이즈 변경 이벤트
  @Output() public onRowCheckBoxClick: EventEmitter<any> = new EventEmitter<any>(); // 체크박스 값 변경 이벤트
  @Output() public selectionChanged: EventEmitter<any> = new EventEmitter<any>(); // 체크박스 선택 이벤트
  @Output() public filterContentChanged: EventEmitter<any> = new EventEmitter<any>(); // 필터 내용 변경

  private _rowHeight = null;

  /**
   * AG Grid API
   */
  private gridApi: GridApi;
  private gridColumnApi: ColumnApi;

  private subscription: Subscription;
  private contextMenuSubscription: Subscription;

  constructor(private dialogService: NbDialogService
    , private platform: Platform) {

    this.columnTypes = {
      textRight: {
        cellClass: 'text-right'
      },
      textCenter: {
        cellClass: 'text-center'
      }
    };
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.contextMenuSubscription) {
      this.contextMenuSubscription.unsubscribe();
    }
  }

  // AG-GRID Function with Handler - Start

  onGridReady(target) {
    this.gridApi = target.api;
    this.gridColumnApi = target.columnApi;

    this.initChangeColumnSize();
  }

  onFirstDataRendered(event) {
    // 첫 데이터 렌더링시 컬럼 사이즈 맞춤설정이 되어있으면 변경한다.
    // 여기서하는 이유는 OA 통계같이 컬럼이 최초 세팅때 없고 중간에 계속 추가되는 경우 적용되지 않기 때문

    if (this.useColumnSizeToFit) {
      this.columnSizeFit = true;
      this.gridColumnApi.autoSizeAllColumns(false);
      this.isUseColumnSizeFit();
    }
  }

  onColumnSizeFit() {
    if (this.columnSizeFit)
      this.gridApi.sizeColumnsToFit();
    else
      this.gridColumnApi.autoSizeAllColumns(false);

    this.columnSizeFit = !this.columnSizeFit;
  }

  /**
   * 페이지 변경 시 onChangedPage 이벤트에 조회할 페이지 번호를 담아 전달한다.
   * @param currentPage
   */
  onChangedPage(currentPage): void {
    this.dataSource.number = currentPage;
    this.changePage.emit((currentPage).toString());
  }

  onChangeData(event) {
    if (this.gridApi) {
      if (this.dataSource.content.length == 0)
        this.gridApi.showNoRowsOverlay();
      else
        this.gridApi.hideOverlay();
    }
  }

  updateRow(data) {
    data.forEach(d => {
      const rowNode = this.gridApi.getRowNode(d.nodeId);
      if (rowNode) {
        d.__updated = true;
        rowNode.setData(d);
        this.gridApi.refreshCells({
          rowNodes: [rowNode],
          force: true,
        });
      }
    });
  }

  initChangeColumnSize(): void {
    if (this.gridColumnApi && this.useColumnSizeToFit) {
      this.changeColumnSize();
    }
  }

  changeColumnSize(): void {
    if (this.columnSizeFit)
      this.gridApi.sizeColumnsToFit();
    else
      this.gridColumnApi.autoSizeAllColumns(false);

    this.isUseColumnSizeFit();
    this.columnSizeFit = !this.columnSizeFit;
  }

  onChangedPageSize(pageSize: number): void {
    this.changePageSize.emit(pageSize);
  }

  /**
   * 데이터그리드의 컬럼크기맞춤 사용 시, 해당 데이터그리드 컬럼 적용 시 여백이 생기는 경우 표시되는 컬럼옵션에 
   * 각각 여백만큼의 공간을 균등하게 분배하여 여백을 제거 시키도록 만드는 코드
   * 
   * 1. 화면의 데이터 그리드 너비를 획득 
   * 2. 데이터 그리드의 표시 중인 컬럼중, 최대너비(MaxWidth)가 지정되지 않은 컬럼의 '개수' 및 '너비 합'을 획득
   * 3. (화면의 데이터 그리드 컬럼 너비 - 각 컬럼 너비 합) / 컬럼 개수 에 해당하는 픽셀 획득
   * 4. 획득한 픽셀을 최대너비가 지정되지 않은 컬럼 너비에 추가
   * 
   * TODO: 세로스크롤이 발생하지 않는 양의 데이터를 조회시에도 세로스크롤 가로 넓이만큼 우측 여백이 발생하여 수정 필요
   */
   isUseColumnSizeFit(): void {
    let sumColumnWidth = 0;
    let diffWidthSize = 0;
    let sumColumnCount = 0;

    this.gridColumnApi.getColumnState().forEach(column => {
      if (!column.hide) {
        sumColumnWidth += column.width;

        const col = this.gridColumnApi.getAllColumns().find((c: any) => c.colId == column.colId);

        if (col && col.getMaxWidth() === null) sumColumnCount++;
      }
    });

    if (sumColumnCount < 1) return;

    //데이터그리드가 전체화면에 표기될 경우, 기본 여백 2px 존재함. 3이상을 보정하지 않을 경우 전체화면일 때도 여백을 계산하여 표기됨
    if (this.gridDiv.nativeElement.offsetWidth > sumColumnWidth + 3) {

      diffWidthSize = this.gridDiv.nativeElement.offsetWidth - sumColumnWidth - this.correctionSize;

      //각 컬럼에 추가할 컬럼 너비 
      const addWidthSize = (diffWidthSize / sumColumnCount);

      this.gridColumnApi.getColumnState().forEach(column => {
        if (!column.hide) {

          const col = this.gridColumnApi.getAllColumns().find((c: any) => c.colId == column.colId);

          if (col && col.getMaxWidth() === null) this.gridColumnApi.setColumnWidth(column.colId, column.width + addWidthSize, true);
        }
      });
    }
  }

  /**
   * 언어설정 변경 이벤트 받을 시 컬럼명을 변경 후 세팅에 강제로 바인딩한다.
   */
  private changedLocale(): void {
    this.makeColumnName().then((res) => {
      this.settings = Object.assign({}, res);
    });
  }

  onSelectedRow(event?: any): void {
    if (!event) {
      return;
    }
    this.onMultiRowSelect.emit(event);
  }

  onRowSelect(event?: any): void {
    if (!event) {
      return;
    }
    this.onMultiRowSelect.emit(event);
  }

  /**
   * 필터 API - Start
   */

  public setFilter(column: string, value: any, type: FilterType, condition: FilterCondition) {
    let fileterModel = this.gridApi.getFilterModel() || {};
    fileterModel[column] = {
      filterType: type,
      type: condition,
      filter: value
    };
    this.gridApi.setFilterModel(fileterModel);
  }

  public onFilterContentChanged(event: any) {
    if (!event) return;
    this.filterContentChanged.emit(event);
  }

  public setFilterModel(column: string, filterModel: any) {
    let fileterInstance = this.gridApi.getFilterInstance(column);
    fileterInstance.setModel(filterModel);
    this.gridApi.onFilterChanged();
  }

  public resetFilter(column: string) {
    let fileterModel = this.gridApi.getFilterModel() || {};
    delete fileterModel[column];
    this.gridApi.setFilterModel(fileterModel);
  }

  public resetAllFilter() {
    this.gridApi.setFilterModel(null);
  }

  /**
   * 필터 API - End
   */



  /**
   * 관리자 웹 언어설정 변경 시 컬럼명도 변경한다.
   */
  private makeColumnName(): Promise<any> {
    return new Promise((resolve, reject) => {
      
      if (!Array.isArray(this.settings.columns)) {
        this.settings.columns = [];
      }

      resolve(this.settings);
    });
  }

  /**
   * 컬럼옵션 다이얼로그 오픈
   */
  openColumnOption(): void {
    const columnOptionDialog = this.dialogService.open(ColumnOptionComponent, {
      context: { columns: this.settings.columns },
      hasScroll: true,
    });

    columnOptionDialog.onClose.subscribe((res) => {
      if (res) {
        this.changeVisibleColumns(res);
      }
    });
  }

  /**
   * CSV 내보내기 다이얼로그 오픈
   */
  openExportToCsv(): void {
    const filteName: string = Strings.isBlank(this.fileName) ? this.label : this.fileName;

    const csvDialog = this.dialogService.open(CsvComponent, {
      context: {
        gridApi: this.gridApi,
        fileName: filteName,
      },
    });

    csvDialog.onClose.subscribe((res) => {
      if (res) {
        // this.saveHistory();
      }
    });
  }

  /**
   * 전달받은 컬럼목록에서 visible 여부에 따라 표시할 컬럼을 세팅한다.
   * @param columns
   */
  changeVisibleColumns(columns): void {

    const changedColumns = Objects.clone(columns);

    // if (this._cacheId) {
    //   let dataGridCache: CacheItem<Object> = Caches.dataGridCache;
    //   let cachedDatagrid: any = new Object();

    //   if (dataGridCache.hasData) {
    //     cachedDatagrid = dataGridCache.data;
    //   }

    //   cachedDatagrid[this._cacheId] = changedColumns;
    //   dataGridCache.data = cachedDatagrid;
    // }

    if (typeof this.gridApi !== 'undefined') {
      this.gridApi.setColumnDefs(changedColumns);
      if (this.columnSizeFit) this.gridColumnApi.autoSizeAllColumns(false);
    }

    this.changedLocale();
  }

  setColumns(columns: any[], key: keyof ColDef, value: any) {

    if (!columns) return;

    for (let i = 0; i < columns.length; i++) {
      let column = columns[i];
      if (column.hasOwnProperty('groupId') && column.children) {
        this.setColumns(column.children, key, value);
      }

      if (!column.hasOwnProperty('checkboxSelection'))
        column[key.toString()] = value; // TODO 체크박스용 컬럼필터 생성시 제거
    }
  }

  selectAllEvent() {
    this.gridApi.selectAll();
  }

  unselectAllEvent() {
    this.gridApi.deselectAll();
  }

  selectedItems(): any[] {
    let selectedList: any[] = [];
    this.gridApi.forEachNode((node: RowNode) => {
      if (node.isSelected())
        selectedList.push(node.data);
    });
    return selectedList;
    // return this.gridApi.getSelectedRows();
  }
  
  selectedNodes(): any[] {
    return this.gridApi.getSelectedNodes();
  }

  forEachNode(callback: (node: any) => void): void {
    this.gridApi?.forEachNode(callback);
  }

  getRowNode(id: string): any {
    return this.gridApi?.getRowNode(id);
  }

  handleCellDoubleClicked(event) {
    if (this.rowDoubleClickEnable) {
      this.rowDoubleClick.emit(event);
    }
  }

  handleCellClicked(event) {
    if (this.rowClickEnable) {
      this.rowClick.emit(event);
    }
  }

  /**
   * 데이터그리드에서 체크박스를 체크 하는 경우 이벤트가 발생
   * isCheckBoxChangeEvent true 인 경우에만 발생함으로 체크박스 변경 이벤트 감지를 위해선
   * 데이터그리드 isCheckBoxChangeEvent true로 전달 (기본값 false)
   * 
   */
   onSelectionChanged($event) {
    if (!this.isCheckBoxChangeEvent) return;
    this.selectionChanged.emit();
    // this.onRowCheckBoxClick.emit();
  }

  handleCellClickedByMobile(event) {
    if (this.platform.IOS || this.platform.ANDROID) {
      this.handleCellDoubleClicked(event);
    }
  }
}

export declare type FilterType = 'text' | 'number' | 'date';
export declare type FilterCondition =
  | 'equals'
  | 'notEqual'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'inRange'
  | 'empty';